import { create } from 'zustand'
import type {
  ProgressEvent,
  ScanStageKey,
  StageStatus,
  LocateBusinessScene,
  CompetitorMapSceneData,
  ReviewsSceneData,
  PhotosSceneData,
  WebsiteAuditSceneData,
  ScoreRevealSceneData,
} from '../types'

import { STAGE_ORDER } from '../types'

interface StageState {
  key: ScanStageKey
  status: StageStatus
  message: string
  percent: number
  scene_payload?: string
}

interface ScanStore {
  scanId: string | null
  businessId: string | null
  businessName: string | null
  /** GPS coordinates seeded from SearchPage at scan init time — available before the first SSE event. */
  businessLat: number | null
  businessLng: number | null
  overallPercent: number
  stages: Record<ScanStageKey, StageState>
  events: ProgressEvent[]
  isRunning: boolean
  isCompleted: boolean
  isFailed: boolean
  reportReady: boolean

  // ── Typed scene data parsed from SSE scene_payloads ─────────────────────
  /** Stage 1 payload: business GPS coords + name for the map zoom-in. */
  locateScene: LocateBusinessScene | null
  /** Stage 2 payload: business pin + all competitor pins for the map tour. */
  competitorMapScene: CompetitorMapSceneData | null
  /** Stage 5 payload: real review cards to animate. */
  reviewsScene: ReviewsSceneData | null
  /** Stage 6 payload: photo stack thumbnails + categories. */
  photosScene: PhotosSceneData | null
  /** Stage 7 payload: website signals for the "browser inspector" animation. */
  websiteAuditScene: WebsiteAuditSceneData | null
  /** Stage 8 payload: real overall + category scores for the score-reveal animation. */
  scoreRevealScene: ScoreRevealSceneData | null

  /**

   * True once the MapScene animation (business zoom-in + full competitor tour
   * + zoom-out overview) has fully completed. Used by ScanPage to hold the map
   * visible until the entire animation plays out — regardless of how fast the
   * backend stages progress.
   */
  mapAnimationDone: boolean

  initScan: (
    scanId: string,
    businessId: string,
    businessName: string,
    lat?: number,
    lng?: number,
  ) => void
  applyEvent: (ev: ProgressEvent) => void
  markCompleted: () => void
  markFailed: () => void
  markReportReady: () => void
  setMapAnimationDone: () => void
  reset: () => void
}

const defaultStages = (): Record<ScanStageKey, StageState> => {
  const stages = {} as Record<ScanStageKey, StageState>
  for (const key of STAGE_ORDER) {
    stages[key] = { key, status: 'pending', message: '', percent: 0 }
  }
  return stages
}

export const useScanStore = create<ScanStore>((set, get) => ({
  scanId: null,
  businessId: null,
  businessName: null,
  businessLat: null,
  businessLng: null,
  overallPercent: 0,
  stages: defaultStages(),
  events: [],
  isRunning: false,
  isCompleted: false,
  isFailed: false,
  reportReady: false,

  locateScene: null,
  competitorMapScene: null,
  reviewsScene: null,
  photosScene: null,
  websiteAuditScene: null,
  scoreRevealScene: null,
  mapAnimationDone: false,

  initScan: (scanId, businessId, businessName, lat, lng) => {
    set({
      scanId,
      businessId,
      businessName,
      businessLat: lat ?? null,
      businessLng: lng ?? null,
      overallPercent: 0,
      stages: defaultStages(),
      events: [],
      isRunning: true,
      isCompleted: false,
      isFailed: false,
      reportReady: false,
      locateScene: null,
      competitorMapScene: null,
      reviewsScene: null,
      photosScene: null,
      websiteAuditScene: null,
      scoreRevealScene: null,
      mapAnimationDone: false,
    })
  },


  applyEvent: (ev) => {
    set((state) => {
      const existing = state.stages[ev.stage_key]

      // Preserve the previous scene_payload if the new event doesn't carry one.
      // This keeps the scene data visible across status transitions (running → completed).
      const scenePayload = ev.scene_payload ?? existing?.scene_payload

      const stages = { ...state.stages }
      stages[ev.stage_key] = {
        key: ev.stage_key,
        status: ev.status,
        message: ev.message,
        percent: ev.percent,
        scene_payload: scenePayload,
      }

      const updates: Partial<ScanStore> = {
        stages,
        overallPercent: ev.percent,
        events: [...state.events, ev],
      }

      // Only parse NEW scene_payloads (not preserved ones) to avoid re-parsing on every event.
      if (ev.scene_payload) {
        try {
          const scene = JSON.parse(ev.scene_payload)
          switch (scene.type) {
            case 'locate_business':
              updates.locateScene = scene as LocateBusinessScene
              break
            case 'competitor_map':
              updates.competitorMapScene = scene as CompetitorMapSceneData
              break
            case 'reviews':
              updates.reviewsScene = scene as ReviewsSceneData
              break
            case 'photos':
              updates.photosScene = scene as PhotosSceneData
              break
            case 'website_audit':
              updates.websiteAuditScene = scene as WebsiteAuditSceneData
              break
            case 'score_reveal':
              updates.scoreRevealScene = scene as ScoreRevealSceneData
              break
          }

        } catch { /* ignore malformed payloads */ }
      }

      return updates
    })

    // Check if scan is complete from the finalize_report stage
    const st = get().stages['finalize_report']
    if (st?.status === 'completed') {
      get().markCompleted()
    }
  },

  markCompleted: () => set({ isCompleted: true, isRunning: false, overallPercent: 100 }),
  markFailed: () => set({ isFailed: true, isRunning: false }),
  markReportReady: () => set({ reportReady: true }),
  setMapAnimationDone: () => set({ mapAnimationDone: true }),

  reset: () =>
    set({
      scanId: null,
      businessId: null,
      businessName: null,
      businessLat: null,
      businessLng: null,
      overallPercent: 0,
      stages: defaultStages(),
      events: [],
      isRunning: false,
      isCompleted: false,
      isFailed: false,
      reportReady: false,
      locateScene: null,
      competitorMapScene: null,
      reviewsScene: null,
      photosScene: null,
      websiteAuditScene: null,
      scoreRevealScene: null,
      mapAnimationDone: false,
    }),
}))

